'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { type ProductDeliveryData } from '@/fo/feature/product/info/main';
import { type StorePoint } from '@/fo/feature/shops/page/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiPaymentBnplBadge } from '@/fo/ui/payment/bnpl/badge';
import { UiProductCompareButton } from '@/fo/ui/product/compare/button';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import { UiProductInfoShipping } from '@/fo/ui/product/info/shipping';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventElementVisibility } from '@/fo/util/event-element-visibility';
import { MappedProduct, Option } from '@/fo/util/map-product-data';

import { styles } from './UiProductInfoMain.styles';

export const EVENT_NAME_PRODUCT_INFO_MAIN_SHOW = 'UiProductInfoMainShow';
export const EVENT_NAME_PRODUCT_INFO_MAIN_HIDE = 'UiProductInfoMainHide';
export interface UiProductInfoMainProps {
  type?: 'pdp' | 'catalog';
  isAuthorized: boolean;
  isInWishlist: boolean;
  isInCompare: boolean;
  isCartLoading: boolean;
  selectedProduct: MappedProduct;
  baseProduct: MappedProduct;
  cartCount?: number;
  className?: string;
  maxStockQuantity: number;
  onWishlistClick?: () => void;
  onCompareClick?: () => void;
  onCartClick?: () => void;
  onCartChange?: (quantity: number) => void;
  goToDescription?: () => void;
  onStockClick?: () => void;
  onSelectCity?: (city: string) => void;
  isConfigurableProduct?: boolean;
  onOptionChange?: (product: MappedProduct | null) => void;
  storesData?: {
    stores: StorePoint[];
    error?: string | null;
    productDeliveryData: ProductDeliveryData | null;
  };
  isLoading?: boolean;
}

export function UiProductInfoMain({
  type = 'pdp',
  isInWishlist,
  isInCompare,
  isCartLoading,
  selectedProduct,
  baseProduct,
  cartCount = 0,
  maxStockQuantity,
  className,
  onWishlistClick,
  onCompareClick,
  onCartClick,
  onCartChange,
  onOptionChange,
  storesData,
  onSelectCity,
  isLoading = false
}: UiProductInfoMainProps) {
  const slots = styles({ className });
  const t = useTranslations();
  const [product, setProduct] = useState(selectedProduct);

  const [configurableVariants, setConfigurableVariants] = useState(
    selectedProduct.configurableVariants
  );
  const [deliveryDrawer, setDeliveryDrawer] = useState(false);
  const buyButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((newProduct) => {
      setConfigurableVariants((prev) =>
        prev?.map((option) => ({
          ...option,
          isSelected:
            newProduct.configurableVariants?.find(
              (item) => option.sku === item.sku
            )?.isSelected ?? false
        }))
      );

      setProduct(newProduct);

      onOptionChange?.(newProduct);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!buyButtonRef.current || type !== 'pdp') return;

    const getHeaderHeight = () => {
      const height = getComputedStyle(document.documentElement)
        .getPropertyValue('--head-fixed-height')
        .replace('px', '');
      return height ? parseInt(height, 10) : 0;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          // Элемент виден во viewport - скрываем фиксированную кнопку
          utilEventElementVisibility.hide(EVENT_NAME_PRODUCT_INFO_MAIN_HIDE);
        } else {
          // Элемент не виден - проверяем находится ли он выше viewport
          const buttonRect = entry.target.getBoundingClientRect();
          const headerHeight = getHeaderHeight();
          const isButtonAboveViewport = buttonRect.bottom < headerHeight;

          if (isButtonAboveViewport) {
            utilEventElementVisibility.show(EVENT_NAME_PRODUCT_INFO_MAIN_SHOW);
          } else {
            utilEventElementVisibility.hide(EVENT_NAME_PRODUCT_INFO_MAIN_HIDE);
          }
        }
      },
      {
        rootMargin: `-${getHeaderHeight()}px 0px 0px 0px`,
        threshold: [0, 1]
      }
    );

    observer.observe(buyButtonRef.current);

    return () => {
      observer.disconnect();
    };
  }, [type]);

  const onOptionClick = (option: Option) => {
    const { product: newProduct } = option;
    const isAllConfigurableDisabled =
      configurableVariants?.length &&
      configurableVariants?.filter((item) => !item.isSelected).length ===
        configurableVariants?.length;

    if (!newProduct) {
      return;
    }
    newProduct.parentSku = product.parentSku ?? product.sku;

    utilEventConfigurableProduct.selectProduct(newProduct);

    setProduct(newProduct);

    setConfigurableVariants((prev) =>
      prev?.map((item, index) => ({
        ...item,
        isSelected:
          index === 0 && isAllConfigurableDisabled
            ? true
            : item.sku === newProduct.sku
      }))
    );

    onOptionChange?.(newProduct);
  };

  const handleClickShipping = () => {
    setDeliveryDrawer(true);
  };

  useEffect(() => {
    const selected =
      configurableVariants?.find((variant) => variant.isSelected) ??
      configurableVariants?.[0];

    if (selected?.product) {
      onOptionClick(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={slots.base({ className })}>
        <div className={slots.block()}>
          <div className={slots.info({ catalog: type === 'catalog' })}>
            {type === 'catalog' && selectedProduct.thumbnail && (
              <Image
                className={slots.image({ catalog: type === 'catalog' })}
                src={selectedProduct.thumbnail.src}
                width={120}
                height={120}
                alt={selectedProduct.thumbnail.alt}
              />
            )}

            <div>
              <h1 className={slots.name({ catalog: type === 'catalog' })}>
                <span
                  className={slots.nameText({ catalog: type === 'catalog' })}
                  dangerouslySetInnerHTML={{ __html: product.name }}
                />
                {!!baseProduct.shortDescription && (
                  <span
                    className={slots.shortDescription({
                      catalog: type === 'catalog'
                    })}
                    dangerouslySetInnerHTML={{
                      __html: selectedProduct.shortDescription
                    }}
                    suppressHydrationWarning
                  />
                )}
              </h1>

              {product?.ean && (
                <p className={slots.sku()}>
                  {`${t('product.characteristics.article')}: ${product?.ean}`}
                </p>
              )}

              {configurableVariants && (
                <UiProductOptionsBase
                  className="mt-8"
                  size="l"
                  options={configurableVariants}
                  onOptionClick={(option) =>
                    option.isSelected ? null : onOptionClick(option)
                  }
                />
              )}
            </div>
          </div>
        </div>

        <div>
          <div className={slots.block()}>
            {product.basePrice !== null && product.basePrice > 0 && (
              <UiKitPriceBase
                value={product.finalPrice ?? product.basePrice}
                oldValue={product.finalPrice ? product.basePrice : null}
                className={slots.price({
                  className:
                    product.stockStatus === 'OUT_OF_STOCK'
                      ? 'text-text-inactive'
                      : ''
                })}
                classNameValue="heading-3xl font-medium"
                classNameOldValue="heading-m leading-1.4"
                currency={product.currency}
              />
            )}
          </div>

          <div className={slots.actions()}>
            {cartCount > 0 && (
              <UiProductCounterInputBase
                onDebounceChange={onCartChange}
                value={cartCount}
                loading={isCartLoading && cartCount > 0}
                max={maxStockQuantity}
              />
            )}

            {!cartCount && (
              <UiKitButtonBase
                className={slots.addToCart()}
                onClick={onCartClick}
                ref={buyButtonRef}
                loading={isCartLoading && cartCount === 0}
                color="secondary"
                disabled={product.stockStatus === 'OUT_OF_STOCK'}
              >
                {product.stockStatus === 'OUT_OF_STOCK' && t('Out of stock')}
                {product.stockStatus === 'IN_STOCK' &&
                  cartCount > 0 &&
                  t('In cart')}
                {product.stockStatus === 'IN_STOCK' &&
                  cartCount === 0 &&
                  t('To cart')}
              </UiKitButtonBase>
            )}

            <div className={slots.itemActions()}>
              <UiProductCompareButton
                className={slots.compare()}
                isActive={isInCompare}
                onClick={onCompareClick}
              />
              <UiProductWishlistButton
                className={slots.wishlistIcon()}
                isActive={isInWishlist}
                onClick={onWishlistClick}
              />
            </div>
          </div>

          {product.stockStatus === 'IN_STOCK' && (
            <UiPaymentBnplBadge
              amount={product.finalPrice ?? product.basePrice ?? 0}
              size="m"
              variant="detailed"
              color="transparent"
              theme="light"
              align="left"
            />
          )}
        </div>
        <div className={slots.shipping()}>
          <span
            className={slots.shippingLbl()}
            onClick={() => handleClickShipping()}
          >
            {t('Free shipping')}
          </span>{' '}
          {t('acrossRussia')}
        </div>
      </div>

      <UiProductInfoShipping
        open={deliveryDrawer}
        onOpenChange={setDeliveryDrawer}
        storesData={storesData}
        onSelectCity={onSelectCity}
        isLoading={isLoading}
      />
    </>
  );
}
